import { useCallback, useEffect, useRef, useState } from 'react';

const KSP_ORIGIN_RE = /^https:\/\/(?:[a-z0-9-]+\.)?ksp\.co\.il$/;
const LOCALHOST_DEV_RE = /^http:\/\/localhost:\d+$/;

export interface UseExtensionAuthOptions {
  iframeId: string;
  parentOrigin: string;
  timeoutMs?: number; // default 2000
}

export type ExtensionAuthStatus =
  | { state: 'pending' }
  | { state: 'authenticated'; token: string; expiresAt: Date }
  | { state: 'unauthenticated' }
  | { state: 'invalid-origin' };

export function isAllowedParentOrigin(origin: string): boolean {
  if (KSP_ORIGIN_RE.test(origin)) return true;
  if (import.meta.env.DEV && LOCALHOST_DEV_RE.test(origin)) return true;
  return false;
}

// Internal alias kept for callers in this module.
const isAllowedOrigin = isAllowedParentOrigin;

/**
 * Iframe-side nonce-challenge handshake with the extension content script.
 *
 * Sequence:
 *   iframe → parent:  { type: 'PRICEDROP_AUTH_REQUEST', nonce, iframeId }  (targetOrigin = parentOrigin)
 *   parent → iframe:  { type: 'PRICEDROP_AUTH_TOKEN',  nonce, iframeId, token, expiresAt }
 *
 * Returned functions:
 *   - `reportAuthExpired()`: call on API 401 mid-session. Posts
 *     PRICEDROP_AUTH_EXPIRED (signals the parent to pre-invalidate its
 *     cached token) and restarts the handshake with a fresh nonce.
 */
export function useExtensionAuth(opts: UseExtensionAuthOptions): {
  status: ExtensionAuthStatus;
  reportAuthExpired: () => void;
} {
  const { iframeId, parentOrigin, timeoutMs = 2000 } = opts;
  const [status, setStatus] = useState<ExtensionAuthStatus>({ state: 'pending' });
  const nonceRef = useRef<string | null>(null);

  useEffect(() => {
    if (!iframeId || !isAllowedOrigin(parentOrigin)) {
      setStatus({ state: 'invalid-origin' });
      return;
    }

    const nonce = crypto.randomUUID();
    nonceRef.current = nonce;

    const listener = (event: MessageEvent) => {
      if (event.source !== window.parent) return;
      if (event.origin !== parentOrigin) return;
      const data = event.data as
        | {
            type: 'PRICEDROP_AUTH_TOKEN';
            nonce: string;
            iframeId: string;
            token: string;
            expiresAt: string;
          }
        | undefined;
      if (!data || data.type !== 'PRICEDROP_AUTH_TOKEN') return;
      if (data.nonce !== nonceRef.current) return;
      if (data.iframeId !== iframeId) return;

      setStatus({
        state: 'authenticated',
        token: data.token,
        expiresAt: new Date(data.expiresAt),
      });
    };

    window.addEventListener('message', listener);
    window.parent.postMessage({ type: 'PRICEDROP_AUTH_REQUEST', nonce, iframeId }, parentOrigin);

    const timeout = setTimeout(() => {
      setStatus((prev) => (prev.state === 'pending' ? { state: 'unauthenticated' } : prev));
    }, timeoutMs);

    return () => {
      window.removeEventListener('message', listener);
      clearTimeout(timeout);
    };
  }, [iframeId, parentOrigin, timeoutMs]);

  // Wrapped in useCallback so consumer effects that depend on
  // reportAuthExpired don't re-run on every render. Without this, the
  // returned function gets a fresh reference each render, poisoning consumer
  // effect dependency arrays and causing re-fetch loops.
  const reportAuthExpired = useCallback(() => {
    if (!isAllowedOrigin(parentOrigin)) return;
    // First signal that the previously-issued token is dead so the parent
    // pre-invalidates its cache, then restart the handshake with a fresh
    // nonce so the parent's reply targets THIS new request.
    window.parent.postMessage({ type: 'PRICEDROP_AUTH_EXPIRED', iframeId }, parentOrigin);
    const nonce = crypto.randomUUID();
    nonceRef.current = nonce;
    setStatus({ state: 'pending' });
    window.parent.postMessage({ type: 'PRICEDROP_AUTH_REQUEST', nonce, iframeId }, parentOrigin);
  }, [iframeId, parentOrigin]);

  return { status, reportAuthExpired };
}
